import { Component, Event, EventEmitter, h, Prop, State } from '@stencil/core';
import { addMonths, addYears, format, isAfter, isPast, isSameDay, isSameMonth, max, startOfDecade, startOfMonth, startOfYear, subMonths } from 'date-fns';
import { localeToFnsLocale } from '../../../../utils/trans';
import { DayState, DayConfig, DaysAvailability, EnabledDisabled } from '../../../../types/types';
import { makeDatesForMonth, renderDecadeView, renderViewSwitch, renderWeekDays, renderYearView, SwitchView } from './utils';
import { t } from '../../../../redux/utils';
import { CalendarMode } from '../../rentware-date-select/utils';
import { ButtonSize, ButtonStyle } from '../../rentware-button/types';
import { store } from '@stencil/redux';
import { Unsubscribe } from 'redux';
import { RootState } from '../../../../redux/reducers';

@Component({
  tag: 'rentware-range-picker',
  styleUrl: 'style.sass',
  shadow: true,
})
export class RentwareItemRangePicker {
  @Prop() calendarMode: CalendarMode = CalendarMode.Range; // Only for icon and tooltips
  @Prop() firstDayOfWeek: number = 1;
  @Prop() numberOfMonths: number = 1;
  @Prop() locale: string;
  @Prop() withDateRange: boolean;
  @Prop() rangeLoading: boolean;
  @Prop() daysAvailability: DaysAvailability;
  @Prop() bookingDate: Date = null;
  @Prop() endDate: Date = null;
  @Prop() suspectedEndDate: Date = null;
  @Prop() autoClose: boolean = false;
  @Prop() monthAtStart: Date = null;
  @Prop() firstAvailableDate: Date = null;
  @Prop() lastAvailableDate: Date = null;
  @Prop() showNotAvailableOverlay: boolean; // requires onResetCalendar function to work
  @Prop() showLocationNotSelectedOverlay: boolean;
  @Prop() onRangeDayClick: (day: Date) => void;
  @Prop() onResetCalendar: (day?: Date) => void;
  @Prop() onRangeMonthClick: (month: Date) => void;
  @State() currentDate: Date;
  @State() currentHoverDate: Date;
  @State() selectedCalendarDate: Date;
  @State() switchView: SwitchView = SwitchView.Month;
  @Prop({ mutable: true }) partiallyAvailableDays: EnabledDisabled;

  @Event() clickOutsideCalendar: EventEmitter;

  unsubscribe!: Unsubscribe;

  componentWillLoad() {
    this.unsubscribe = store.mapStateToProps(this, (state: RootState) => {
      return {
        partiallyAvailableDays: state.system.widgetConfig.advanced.calendar.showPartiallyAvailable,
      };
    });

    this.selectedCalendarDate = this.monthAtStart || this.bookingDate || new Date();
  }

  disconnectedCallback() {
    this.unsubscribe();
  }

  handlePreviousClick = () => {
    if (this.switchView === SwitchView.Decade) {
      this.selectedCalendarDate = addYears(startOfYear(this.selectedCalendarDate), -10);
    } else if (this.switchView === SwitchView.Year) {
      this.selectedCalendarDate = addYears(startOfMonth(this.selectedCalendarDate), -1);
    } else {
      this.selectedCalendarDate = addMonths(startOfMonth(this.selectedCalendarDate), -1);
      this.onRangeMonthClick?.(this.selectedCalendarDate);
    }
  };

  get previousButtonDisabled() {
    if (!this.daysAvailability) {
      return false;
    }

    if (this.switchView === SwitchView.Decade) {
      return isPast(startOfDecade(this.selectedCalendarDate));
    } else if (this.switchView === SwitchView.Year) {
      return isPast(startOfYear(this.selectedCalendarDate));
    } else {
      return isPast(startOfMonth(this.selectedCalendarDate));
    }
  }

  handleNextClick = () => {
    if (this.switchView === SwitchView.Decade) {
      this.selectedCalendarDate = addYears(startOfYear(this.selectedCalendarDate), 10);
    } else if (this.switchView === SwitchView.Year) {
      this.selectedCalendarDate = addYears(startOfMonth(this.selectedCalendarDate), 1);
    } else {
      this.selectedCalendarDate = addMonths(startOfMonth(this.selectedCalendarDate), 1);
      this.onRangeMonthClick?.(this.selectedCalendarDate);
    }
  };

  handleNextDropOffClick = () => {
    this.selectedCalendarDate = startOfMonth(this.firstAvailableDate);
    this.onRangeMonthClick?.(this.selectedCalendarDate);
  };

  handleDayClick = (item: DayConfig) => {
    this.currentHoverDate = null;

    const readyToBooking = (item.dayState === DayState.Selectable || item.dayState === DayState.SelectablePartially) && !item.clickToReset;

    if (readyToBooking) {
      this.onRangeDayClick?.(item.date);
      if (this.autoClose) {
        this.clickOutsideCalendar.emit();
      }
    } else if (item.clickToReset) {
      this.onResetCalendar?.(item.date);
    }
  };

  handleDayHover = (item: DayConfig) => {
    if (this.isHoverActive && item.isAfterOrOnPickup) {
      this.currentHoverDate = item.date;
    }
  };

  handleNotDayHover = () => {
    this.currentHoverDate = null;
  };

  changeCalendarDate = (date: Date, updateParent: boolean = true) => {
    updateParent && this.onRangeMonthClick?.(date);
    this.selectedCalendarDate = date;
  };

  setSwitchView = (view: SwitchView) => {
    this.switchView = view;
  };

  get onlyDropOffOnPickup(): boolean {
    return (
      !!this.bookingDate && // 1. Pick up is required
      !this.endDate && // 2. Drop off is not picked
      this.lastAvailableDate && // 3. Last available date is known
      isSameDay(this.bookingDate, this.lastAvailableDate)
    );
  }

  get isHoverActive(): boolean {
    return (
      this.bookingDate && // 1. Pick up is required
      !this.endDate && // 2. Drop off is not picked
      this.withDateRange && // 3. Ability to select drop off is required
      !this.onlyDropOffOnPickup && // 4. Drop off is available on any other day than pick up
      !this.rangeLoading // 5. Is not in loading state
    );
  }

  get dates(): DayConfig[] {
    return makeDatesForMonth({
      month: this.selectedCalendarDate,
      isHoverActive: this.isHoverActive,
      bookingDate: this.bookingDate,
      endDate: this.endDate,
      currentHoverDate: this.currentHoverDate,
      withDateRange: this.withDateRange,
      daysAvailability: this.daysAvailability,
      onlyDropOffOnPickup: this.onlyDropOffOnPickup,
      partiallyAvailableDays: this.partiallyAvailableDays === EnabledDisabled.Enabled,
    });
  }

  get datesForSecondMonth(): DayConfig[] {
    return makeDatesForMonth({
      month: addMonths(this.selectedCalendarDate, 1),
      isHoverActive: this.isHoverActive,
      bookingDate: this.bookingDate,
      endDate: this.endDate,
      currentHoverDate: this.currentHoverDate,
      withDateRange: this.withDateRange,
      daysAvailability: this.daysAvailability,
      onlyDropOffOnPickup: this.onlyDropOffOnPickup,
      partiallyAvailableDays: this.partiallyAvailableDays === EnabledDisabled.Enabled,
    });
  }

  get currentLocale() {
    return localeToFnsLocale(this.locale);
  }

  get calendarClass() {
    if (this.numberOfMonths === 2) {
      return 'RangeCalendar DoubleColumn';
    }

    return 'RangeCalendar';
  }

  get showFirstDateHint(): boolean {
    if (!this.firstAvailableDate || !this.selectedCalendarDate || this.switchView !== SwitchView.Month) {
      return false;
    }

    // this will check if anything is visible in multiple month picker
    const firstMonth = subMonths(this.firstAvailableDate, this.numberOfMonths - 1);

    if (!isSameMonth(firstMonth, this.selectedCalendarDate) && isAfter(firstMonth, this.selectedCalendarDate)) {
      return true;
    }

    return false;
  }

  get showOverlay(): boolean {
    return (this.showFirstDateHint || this.showNotAvailableOverlay || this.showLocationNotSelectedOverlay) && !this.bookingDate && !this.rangeLoading;
  }

  handleOverlayClick = () => {
    if (this.showNotAvailableOverlay) {
      const dateToReset = max([this.selectedCalendarDate || new Date(), this.firstAvailableDate || new Date()]);
      this.changeCalendarDate(dateToReset, false);
      this.onResetCalendar(dateToReset);
    } else if (this.showFirstDateHint) {
      this.changeCalendarDate(this.firstAvailableDate);
    }
  };

  get overlayText(): string {
    if (this.showNotAvailableOverlay) {
      return t('articleBooking.notAvailableForSelection');
    } else if (this.showFirstDateHint) {
      return t('articleBooking.notAvailableForMonth');
    } else if (this.showLocationNotSelectedOverlay) {
        return t('articleBooking.missingLocation');
    }
  }

  get overlayButtonHint(): Node {
      const buttonSvg = (
          <svg class='DateButtonSVG' width='7' height='11' viewBox='0 0 7 11' fill='none'
                          xmlns='http://www.w3.org/2000/svg'>
            <path d='M1 10.5L6 5.5L0.999999 0.5' stroke='#46435B' stroke-linecap='round' stroke-linejoin='round' />
          </svg>
      )

      if (this.showNotAvailableOverlay) {
          return (
              <div class='OverlayContent'>
                  {t('articleBooking.resetDate')}

                  {buttonSvg}
              </div>
          );
      } else if (this.showFirstDateHint) {
          return (
              <div class='OverlayContent'>
                  {t('articleBooking.jumpNextAvailable')}

                  {buttonSvg}
              </div>
          );
      }
  }

    renderView() {
        if (this.switchView === SwitchView.Decade) {
            return renderDecadeView({
                allowPast: !this.daysAvailability,
                selectedCalendarDate: this.selectedCalendarDate,
                changeCalendarDate: this.changeCalendarDate,
        setSwitchView: this.setSwitchView,
      });
    }

    if (this.switchView === SwitchView.Year) {
      return renderYearView({
        allowPast: !this.daysAvailability,
        currentLocale: this.currentLocale,
        selectedCalendarDate: this.selectedCalendarDate,
        changeCalendarDate: this.changeCalendarDate,
        setSwitchView: this.setSwitchView,
      });
    }

    const jumpNextMonth =
      this.withDateRange && // only for range calendar
      !!this.bookingDate && // pick up is already selected
      !this.endDate && // drop off is not selected
      // month of first available date is after selected month, including two month shown calendar
      isAfter(startOfMonth(this.firstAvailableDate), startOfMonth(addMonths(this.selectedCalendarDate, this.numberOfMonths - 1)));

    return (
      <div>
        <div class="monthsContainer"></div>

        <rentware-spacer size="S" />

        <div class="DatesContainer">
          <div>
            {renderWeekDays(this.currentLocale)}
            <rentware-spacer size="S" />
            <div class="DateGrid">
              {this.dates.map((day, index) => {
                return (
                  <rentware-range-picker-day
                    day={day}
                    index={index}
                    secondMonth={false}
                    allowPast={!this.daysAvailability}
                    withDateRange={this.withDateRange}
                    calendarMode={this.calendarMode}
                    lastAvailableDate={this.lastAvailableDate}
                    bookingDate={this.bookingDate}
                    endDate={this.endDate}
                    firstDayOfWeek={this.firstDayOfWeek}
                    onNextClick={this.handleNextClick}
                    onDayClick={this.handleDayClick}
                    onDayHover={this.handleDayHover}
                    firstDate={this.dates[0].date}
                    firstAvailableDate={this.firstAvailableDate}
                  />
                );
              })}
            </div>
          </div>
          {this.numberOfMonths === 2 && (
            <div class="SecondDaysContainer">
              {renderWeekDays(this.currentLocale)}

              <rentware-spacer size="S" />

              <div class="DateGrid">
                {this.datesForSecondMonth.map((day, index) => {
                  return (
                    <rentware-range-picker-day
                      day={day}
                      index={index}
                      secondMonth={true}
                      allowPast={!this.daysAvailability}
                      withDateRange={this.withDateRange}
                      calendarMode={this.calendarMode}
                      lastAvailableDate={this.lastAvailableDate}
                      bookingDate={this.bookingDate}
                      endDate={this.endDate}
                      firstDayOfWeek={this.firstDayOfWeek}
                      onNextClick={this.handleNextClick}
                      onDayClick={this.handleDayClick}
                      onDayHover={this.handleDayHover}
                      firstDate={this.dates[0].date}
                      firstAvailableDate={this.firstAvailableDate}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {jumpNextMonth && (
            <div class="JumpNextContainer" onClick={this.handleNextDropOffClick}>
              {t('articleBooking.jumpNextDropOff', { date: format(this.firstAvailableDate, 'PP') })}
              <svg class="DateButtonSVG" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="11.5" />
                <path d="M10.4002 16.8L15.2002 12L10.4002 7.19998" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </div>
          )}
        </div>
      </div>
    );
  }

  render() {
    return (
      <div class={this.calendarClass} onMouseLeave={this.handleNotDayHover}>
        {this.rangeLoading && <rentware-spinner withOverlay={true} />}

        <div class="NavContainer">
          <button onClick={this.handlePreviousClick} class="NavButton" disabled={this.previousButtonDisabled} aria-label={t('accessibility.previousMonth')}>
            <svg class="ArrowSvg" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="11.5" />
              <path d="M13.5998 16.8L8.7998 12L13.5998 7.19998" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>

          {renderViewSwitch({
            switchView: this.switchView,
            selectedCalendarDate: this.selectedCalendarDate,
            setSwitchView: this.setSwitchView,
            numberOfMonths: this.numberOfMonths,
            currentLocale: this.currentLocale,
          })}

          <button onClick={this.handleNextClick} class="NavButton" aria-label={t('accessibility.nextMonth')}>
            <svg class="ArrowSvg" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="11.5" />
              <path d="M10.4002 16.8L15.2002 12L10.4002 7.19998" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
        </div>

        <rentware-spacer size="S" />

        <div class="RangeCalendarView">
          {/* Overlay */}
          <div class={{ FirstDateOverlay: true, ShowOverlay: this.showOverlay }}>
            {this.overlayText}
              {!this.showLocationNotSelectedOverlay &&
                <rentware-button
                  appearance={{
                    fullWidth: true,
                    size: ButtonSize.XL,
                    style: ButtonStyle.Default,
                  }}
                  buttonText={this.overlayButtonHint}
                  onClick={this.handleOverlayClick}
                />
              }
          </div>

          {this.renderView()}
        </div>
      </div>
    );
  }
}
